/**
 * opportunityScore.ts — Núcleo de inteligencia de leads de MapeoData.
 *
 * Implementa los DOS EJES SEPARADOS que pidió el CEO (Fase 3):
 *
 *  1. **Opportunity Score (0-100)** — oportunidad de VENTA. Combina la
 *     REPUTACIÓN del negocio (valoración + nº reseñas) con sus CARENCIAS
 *     VENDIBLES (sin web, ficha no reclamada, pocas/malas reseñas). Encarna
 *     la regla "fuerte pero carente": un negocio con buena reputación PERO
 *     con una carencia detectable es el prospecto ideal.
 *     → NO incluye "sin email"/"sin teléfono": eso es contactabilidad, no
 *       oportunidad de venta.
 *
 *  2. **Contactabilidad** — eje APARTE, NO suma al score. Canales por los que
 *     se puede contactar al lead (email / teléfono / WhatsApp).
 *
 * Solo usa datos INMEDIATOS disponibles hoy en `mp_business_listings`
 * (rating_value, rating_votes_count, url, is_claimed). Las señales premium
 * (web lenta, SSL, SEO, tráfico) NO entran aquí: van como "próximamente" en
 * la UI, no en la fórmula.
 *
 * Módulo PURO y reutilizable: lo consume la Home hoy y la Búsqueda después.
 * Pesos documentados y exportados para que sean auditables/ajustables.
 *
 * @module lib/opportunityScore
 */

/** Entrada mínima para calcular el score (subconjunto de InboxItem/BusinessListing). */
export interface ScoreInput {
  rating_value: number | null
  rating_votes_count: number | null
  url: string | null
  is_claimed: boolean | null
}

/** Banda del semáforo de oportunidad. */
export type OppBand = 'high' | 'mid' | 'low'

/** Una línea del desglose explicable (para el tooltip de hover y la ficha). */
export interface ScoreFactor {
  label: string
  points: number
}

export interface ScoreResult {
  /** 0-100, redondeado. */
  score: number
  band: OppBand
  /** Factores que contribuyeron (solo los > 0), para el desglose explicable. */
  breakdown: ScoreFactor[]
  /** True si es "fuerte pero carente": reputación alta + al menos una carencia. */
  ideal: boolean
}

/**
 * Pesos del Opportunity Score. Documentados y exportados (auditables).
 * Criterio: la carencia más vendible (sin web → diseño web) pesa más; la
 * reputación AMPLIFICA la oportunidad solo cuando existe una carencia.
 */
export const SCORE_WEIGHTS = {
  /** Sin web: la carencia estrella para quien vende presencia online. */
  GAP_NO_WEB: 45,
  /** Ficha de Google no reclamada: entrada fácil (gestión de presencia). */
  GAP_NOT_CLAIMED: 22,
  /** Pocas o ninguna reseña: pitch de reputación/SEO local. */
  GAP_FEW_REVIEWS: 18,
  /** Mala valoración (< 3,5★): pitch de reputación. Excluyente con GAP_FEW_REVIEWS. */
  GAP_BAD_RATING: 14,
  /** Tope de la suma de carencias (deja margen al bonus de reputación). */
  GAP_CAP: 80,
  /** Bonus máximo de reputación cuando HAY al menos una carencia. */
  REP_BONUS_WITH_GAP: 35,
  /** Bonus máximo de reputación cuando NO hay carencia (oportunidad baja). */
  REP_BONUS_NO_GAP: 10,
  /** Umbral de "pocas reseñas". */
  FEW_REVIEWS_THRESHOLD: 15,
  /** Umbral de "mala valoración". */
  BAD_RATING_THRESHOLD: 3.5,
} as const

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

/** Fuerza de reputación 0..1 (valoración + volumen de reseñas). */
function reputationStrength(rating: number | null, votes: number | null): number {
  // 3★ → 0 ; 5★ → 1. Sin rating conocido → 0.2 (incertidumbre baja).
  const ratingScore = rating != null ? clamp((rating - 3) / 2, 0, 1) : 0.2
  // 120+ reseñas → reputación consolidada.
  const votesScore = votes != null ? clamp(votes / 120, 0, 1) : 0
  return 0.55 * ratingScore + 0.45 * votesScore
}

/**
 * Calcula el Opportunity Score (0-100) con su desglose explicable.
 * Ver `SCORE_WEIGHTS` para los pesos y la racional.
 */
export function opportunityScore(input: ScoreInput): ScoreResult {
  const w = SCORE_WEIGHTS
  const hasWeb = !!(input.url && input.url.trim().length > 0)
  const notClaimed = input.is_claimed === false
  const votes = input.rating_votes_count
  const fewReviews = votes == null || votes < w.FEW_REVIEWS_THRESHOLD
  const badRating =
    input.rating_value != null && input.rating_value < w.BAD_RATING_THRESHOLD

  const breakdown: ScoreFactor[] = []
  let gap = 0
  if (!hasWeb) {
    gap += w.GAP_NO_WEB
    breakdown.push({ label: 'Sin web', points: w.GAP_NO_WEB })
  }
  if (notClaimed) {
    gap += w.GAP_NOT_CLAIMED
    breakdown.push({ label: 'Ficha no reclamada', points: w.GAP_NOT_CLAIMED })
  }
  if (fewReviews) {
    gap += w.GAP_FEW_REVIEWS
    breakdown.push({ label: 'Pocas reseñas', points: w.GAP_FEW_REVIEWS })
  } else if (badRating) {
    gap += w.GAP_BAD_RATING
    breakdown.push({ label: 'Valoración baja', points: w.GAP_BAD_RATING })
  }
  gap = Math.min(gap, w.GAP_CAP)

  const hasGap = gap > 0
  const rep = reputationStrength(input.rating_value, input.rating_votes_count)
  const repBonus = Math.round(
    (hasGap ? w.REP_BONUS_WITH_GAP : w.REP_BONUS_NO_GAP) * rep,
  )
  if (repBonus > 0) {
    const ratingLabel =
      input.rating_value != null
        ? `Reputación · ${input.rating_value.toString().replace('.', ',')}★`
        : 'Reputación'
    breakdown.push({ label: ratingLabel, points: repBonus })
  }

  const score = clamp(Math.round(gap + repBonus), 0, 100)
  return {
    score,
    band: scoreBand(score),
    breakdown,
    ideal: hasGap && rep >= 0.7,
  }
}

/** Banda del semáforo: ✅ alto ≥70 · ⚠️ medio 40-69 · ❌ bajo <40. */
export function scoreBand(score: number): OppBand {
  if (score >= 70) return 'high'
  if (score >= 40) return 'mid'
  return 'low'
}

// ============================================================================
// Señales (chips) — derivadas de los mismos datos inmediatos.
// ============================================================================

export type SignalKind = 'web' | 'claim' | 'rev' | 'star'

export interface LeadSignal {
  label: string
  kind: SignalKind
}

/** Chips de señal de oportunidad para tabla/ficha/feed. */
export function leadSignals(input: ScoreInput): LeadSignal[] {
  const out: LeadSignal[] = []
  const hasWeb = !!(input.url && input.url.trim().length > 0)
  const votes = input.rating_votes_count
  if (!hasWeb) out.push({ label: 'Sin web', kind: 'web' })
  if (input.is_claimed === false) out.push({ label: 'No recl.', kind: 'claim' })
  if (votes == null || votes < SCORE_WEIGHTS.FEW_REVIEWS_THRESHOLD) {
    out.push({ label: votes ? `${votes} reseñas` : 'Sin reseñas', kind: 'rev' })
  }
  if (input.rating_value != null && input.rating_value >= 4.5) {
    out.push({ label: `${input.rating_value.toString().replace('.', ',')}★`, kind: 'star' })
  }
  return out
}

// ============================================================================
// Contactabilidad — EJE APARTE, no suma al score.
// ============================================================================

export interface ContactInput {
  email_primary: string | null
  phone: string | null
}

export interface Contactability {
  email: boolean
  phone: boolean
  /** WhatsApp posible si hay teléfono (canal del mercado hispano). */
  whatsapp: boolean
  /** Nº de canales disponibles (0-3). */
  count: number
}

export function contactability(input: ContactInput): Contactability {
  const email = !!(input.email_primary && input.email_primary.trim())
  const phone = !!(input.phone && input.phone.trim())
  const whatsapp = phone
  return { email, phone, whatsapp, count: [email, phone, whatsapp].filter(Boolean).length }
}
